import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode, MouseEvent } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Typography,
    Paper,
    Checkbox,
    Button,
    TextField,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableSortLabel,
    Popover,
    FormGroup,
    FormControlLabel,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortLinesIcon from '@mui/icons-material/Sort';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dayjs from 'dayjs';
import { BackendPayload, FieldDef, FormFieldType, FormResult, RowId } from '../../types/forms';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import useFormulaRunner from '../../hooks/useFormulaRunner';
import { Link, useSearchParams } from 'react-router-dom';
import { downloadCsv, rowsToCsv } from '../../utils/csv.utils';

type FormsResultsTableProps = {
    config: BackendPayload;
    data: FormResult[];
    formName: string;
    skin?: string;
    onEdit?: (id: RowId) => void;
    onDelete?: (id: RowId) => void;
    onDeleteSelected?: (ids: RowId[]) => void | Promise<void>;
    isRemoveLoading?: boolean;
    maxHeight?: number | string;
    placeholder?: string;
    hasActions?: boolean;
    isUserFriendlyTable?: boolean;
    isSortable?: boolean;
    isSearchable?: boolean;
};

type SortDirection = 'asc' | 'desc';

type SortState = {
    col: string;
    direction: SortDirection;
};

const ACTIONS_W = 100;
const ID_W = 130;
const CHECK_W = 46;
const MIDDLE_W = 180;

const URL_PARAM_SEARCH = 'tableSearch';
const URL_PARAM_SORT_BY = 'tableSortBy';
const URL_PARAM_SORT_DIR = 'tableSortDir';
const URL_PARAM_FILTER_PREFIX = 'tf_';
const URL_PARAM_COLUMNS = 'tableColumns';

const BASE_ACTIVITY_FIELD_IDS = new Set([
    'name',
    'assign',
    'application_id',
    'application_name',
    'application_type',
    'action',
    'button_id',
    'status',
    'target_url',
    'actor_user_id',
    'ip_address',
    'referenced_row_id',
    'page_id',
    'criteria',
    'tradeoffs',
    'created_at',
    'activity_visitor_id',
]);

const SortDirectionIcon = ({ direction, ...props }: { direction: 'asc' | 'desc' } & Record<string, any>) => {
    const ArrowIcon = direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;

    return (
        <Box
            component="span"
            {...props}
            sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
            <ArrowIcon sx={{ fontSize: '0.9em' }}/>
            <SortLinesIcon sx={{ fontSize: '0.9em', ml: '-4px' }}/>
        </Box>
    );
};

const isEmptyLike = (v: any) =>
    v === null || v === undefined || v === '' || (
        Array.isArray(v) && v.length === 0
    );

const normalizeValue = (value: any) => {
    return String(value ?? '').trim().toLowerCase();
};

const getRawUserId = (row: any) => {
const value = row.actor_user_id ?? row.user_id ?? row.userId ?? row.userEmail ?? row.user_email;

    if (isEmptyLike(value)) {
        return null;
    }

    return String(value);
};

const GUEST_EMAIL_TRADEOFF_NAME = 'Guest Email';

type TradeoffItem = {
    name?: string;
    value?: any;
};

const parseTradeoffs = (raw: any): TradeoffItem[] => {
    if (isEmptyLike(raw)) {
        return [];
    }

    if (Array.isArray(raw)) {
        return raw;
    }

    if (typeof raw === 'string') {
        try {
            return parseTradeoffs(JSON.parse(raw));
        } catch {
            return [];
        }
    }

    if (typeof raw === 'object' && raw?.name) {
        return [ raw ];
    }

    return [];
};

const getGuestEmailFromTradeoffs = (raw: any): string | null => {
    const guestEmailTradeoff = parseTradeoffs(raw).find(item => {
        return normalizeValue(item?.name) === normalizeValue(GUEST_EMAIL_TRADEOFF_NAME);
    });

    if (isEmptyLike(guestEmailTradeoff?.value)) {
        return null;
    }

    const guestEmail = String(guestEmailTradeoff?.value).trim();

    return guestEmail || null;
};

const getGuestEmail = (row: any): string | null => {
    if (!isEmptyLike(row?.__guestEmail)) {
        const guestEmail = String(row.__guestEmail).trim();

        return guestEmail || null;
    }

    return getGuestEmailFromTradeoffs(row?.tradeoffs);
};

type UserFriendlyIdentity = {
    type: 'user' | 'guestEmail';
    value: string;
};

const getUserFriendlyIdentity = (row: any): UserFriendlyIdentity | null => {
    const userId = getRawUserId(row);

    if (userId && normalizeValue(userId) !== 'unknown') {
        return {
            type: 'user',
            value: userId,
        };
    }

    const guestEmail = getGuestEmail(row);

    if (guestEmail) {
        return {
            type: 'guestEmail',
            value: guestEmail,
        };
    }

    return null;
};

const getUserFriendlyEmail = (row: any): string | null => {
    const userId = getRawUserId(row);

    if (userId && normalizeValue(userId) !== 'unknown') {
        return userId;
    }

    return getGuestEmail(row);
};

const isUrlLikeValue = (value: any) => {
    const normalized = normalizeValue(value);

    return (
        normalized.startsWith('http://') ||
        normalized.startsWith('https://') ||
        normalized.startsWith('www.') ||
        /^[a-z0-9-]+\.[a-z]{2,}/i.test(normalized)
    );
};

const getUrlFieldValue = (field: FieldDef, raw: any): string => {
    const fieldAny = field as any;

    const candidates = [
        raw,
        fieldAny.value,
        field.defaultValue,
        field.src,
        field.options?.[0]?.value,
        field.label,
    ];

    const url = candidates.find(isUrlLikeValue);

    return url ? String(url).trim() : '';
};

const fmt = (raw: any, type: FormFieldType) => {
    if (type === 'date') {
        const d = dayjs(raw);

        return d.isValid()
            ? d.format('h:mma MM/DD/YY').toUpperCase()
            : String(raw ?? '');
    }

    if (Array.isArray(raw) || typeof raw === 'object') {
        return JSON.stringify(raw ?? '');
    }

    return String(raw ?? '');
};

const getFileValue = (row: any, fieldId: string) => {
    const uploadedFileValues = row.uploadedFileValues;

    if (
        uploadedFileValues &&
        typeof uploadedFileValues === 'object' &&
        uploadedFileValues[fieldId]
    ) {
        return uploadedFileValues[fieldId];
    }

    return row[fieldId];
};

const getApplicationId = (row: any) => {
    return row.application_id ?? row.app_id ?? row.applicationId ?? 'Unknown';
};

const getUserFriendlyGroupKey = (
    identity: UserFriendlyIdentity,
    applicationId: string | number,
) => {
    return `${identity.type}:${normalizeValue(identity.value)}-${applicationId}`;
};

const getLastUrlPart = (url: string) => {
    const cleanUrl = String(url).split('?')[0].split('#')[0];
    const parts = cleanUrl.split('/').filter(Boolean);

    return parts[parts.length - 1] || 'Open';
};

const getUniqueUrls = (urls: string[]) => {
    return Array.from(new Set(urls.filter(Boolean)));
};

const isNotifyAction = (action: any) => {
    return String(action || '').trim().toLowerCase() === 'notify';
};

const isExternalLinksField = (field: FieldDef) => {
    const fieldAny = field as any;

    const type = normalizeValue(fieldAny.type);
    const typeBehavior = normalizeValue(fieldAny.typeBehavior ?? fieldAny.typebehavior);
    const id = normalizeValue(fieldAny.id);
    const fieldId = normalizeValue(fieldAny.fieldID ?? fieldAny.fieldId);
    const hasOptions = Array.isArray(fieldAny.options) && fieldAny.options.length > 0;

    if (type === 'url') {
        return false;
    }

    const hasUrlOption = hasOptions && fieldAny.options.some((option: any) => {
        return isUrlLikeValue(option.value);
    });

    if (
        type === 'external_links' ||
        typeBehavior === 'external_links'
    ) {
        return true;
    }

    return (
        hasOptions &&
        hasUrlOption &&
        !BASE_ACTIVITY_FIELD_IDS.has(id) &&
        !BASE_ACTIVITY_FIELD_IDS.has(fieldId)
    );
};

const getExternalLinkFieldNames = (field: FieldDef) => {
    const fieldAny = field as any;

    const optionNames = Array.isArray(fieldAny.options)
        ? fieldAny.options.flatMap((option: any) => [
            option.name,
            option.label,
            option.title,
        ])
        : [];

    return [
        fieldAny.name,
        fieldAny.label,
        fieldAny.title,
        ...optionNames,
    ]
    .filter(Boolean)
    .map(normalizeValue);
};

const getExternalLinkFieldUrls = (field: FieldDef): string[] => {
    const fieldAny = field as any;

    if (!Array.isArray(fieldAny.options)) {
        return [];
    }

    return fieldAny.options.map((option: any) => normalizeValue(option.value)).filter(Boolean);
};

const isExternalLinkClickRow = (row: any) => {
    return normalizeValue(row.button_id) === 'externallink';
};

const hasExternalLinkClickForField = (
    sourceRows: FormResult[],
    groupedRow: any,
    field: FieldDef,
) => {
    const groupedApplicationId = normalizeValue(getApplicationId(groupedRow));
    const groupedIdentity = getUserFriendlyIdentity(groupedRow);
    const externalNames = getExternalLinkFieldNames(field);
    const externalUrls = getExternalLinkFieldUrls(field);
    const groupedSourceRows = Array.isArray(groupedRow?.__sourceRows) ? groupedRow.__sourceRows : null;
    const rowsToCheck = groupedSourceRows || sourceRows;

    return rowsToCheck.some((sourceRow: any) => {
        if (!isExternalLinkClickRow(sourceRow)) {
            return false;
        }

        const sourceApplicationId = normalizeValue(getApplicationId(sourceRow));

        if (!groupedApplicationId || groupedApplicationId !== sourceApplicationId) {
            return false;
        }

        if (!groupedSourceRows) {
            const sourceIdentity = getUserFriendlyIdentity(sourceRow);

            if (
                !groupedIdentity ||
                !sourceIdentity ||
                groupedIdentity.type !== sourceIdentity.type ||
                normalizeValue(groupedIdentity.value) !== normalizeValue(sourceIdentity.value)
            ) {
                return false;
            }
        }

        const actionName = normalizeValue(sourceRow.action);
        const targetUrl = normalizeValue(sourceRow.target_url);

        const isSameExternalName = externalNames.some(name => name === actionName);
        const isSameExternalUrl = externalUrls.includes(targetUrl);

        return isSameExternalName || isSameExternalUrl;
    });
};

const applyExternalLinkStatuses = (
    groupedRows: any[],
    sourceRows: FormResult[],
    externalLinkFields: FieldDef[],
) => {
    if (!externalLinkFields.length) {
        return groupedRows;
    }

    return groupedRows.map((row: any) => {
        const externalStatuses: Record<string, string> = {};

        externalLinkFields.forEach(field => {
            externalStatuses[field.id] = hasExternalLinkClickForField(
                sourceRows,
                row,
                field,
            ) ? 'true' : 'false';
        });

        return {
            ...row,
            ...externalStatuses,
        };
    });
};

const shouldShowAppSettingsButton = (row: any) => {
    return Boolean(
        row?.criteria &&
        row?.tradeoffs &&
        row?.application_id &&
        (
        row?.__appSettingsRowId || row?.id
        ),
    );
};

const isNotifySettingsRow = (row: any) => {
    return isNotifyAction(row?.action) && shouldShowAppSettingsButton(row);
};

const getAppSettingsUrl = (row: any) => {
    const activityRowId = row.__appSettingsRowId || row.id;

    return `${window.location.origin}/apps/${row.application_id}?tradeoff_id=${activityRowId}`;
};

const buildUserFriendlyRows = (rows: FormResult[]) => {
    const groups = new Map<string, any>();
    const pendingUnknownGroups = new Map<string, any>();
    const groupKeys: string[] = [];
    const pendingUnknownKeys: string[] = [];
    let currentKnownGroupKey: string | null = null;

    const createGroup = (row: any) => {
        return {
            applicationId: getApplicationId(row),
            identityType: getUserFriendlyIdentity(row)?.type ?? null,
            guestEmail: getGuestEmail(row),
            lastRow: null,
            lastKnownRow: null,
            lastSettingsRow: null,
            notifyRows: [] as any[],
            targetUrls: [] as string[],
            sourceRows: [] as FormResult[],
        };
    };

    const getOrCreateGroup = (key: string, row: any) => {
        if (!groups.has(key)) {
            groups.set(key, createGroup(row));
            groupKeys.push(key);
        }

        return groups.get(key);
    };

    const getOrCreatePendingUnknownGroup = (key: string, row: any) => {
        if (!pendingUnknownGroups.has(key)) {
            pendingUnknownGroups.set(key, createGroup(row));
            pendingUnknownKeys.push(key);
        }

        return pendingUnknownGroups.get(key);
    };

    const mergeRowToGroup = (group: any, row: any) => {
        group.lastRow = row;
        group.sourceRows.push(row);

        const guestEmail = getGuestEmail(row);

        if (guestEmail) {
            group.guestEmail = guestEmail;
        }

        if (getUserFriendlyIdentity(row)) {
            group.lastKnownRow = row;
        }

        if (!isEmptyLike(row.target_url)) {
            group.targetUrls.push(String(row.target_url));
        }

        if (shouldShowAppSettingsButton(row)) {
            group.lastSettingsRow = row;
        }

        if (isNotifySettingsRow(row)) {
            group.notifyRows.push(row);
        }
    };

    const mergeGroupToGroup = (targetGroup: any, sourceGroup: any) => {
        targetGroup.targetUrls.push(...sourceGroup.targetUrls);
        targetGroup.notifyRows.push(...sourceGroup.notifyRows);
        targetGroup.sourceRows.push(...sourceGroup.sourceRows);

        if (sourceGroup.guestEmail) {
            targetGroup.guestEmail = sourceGroup.guestEmail;
        }

        if (sourceGroup.lastSettingsRow) {
            targetGroup.lastSettingsRow = sourceGroup.lastSettingsRow;
        }

        if (sourceGroup.lastKnownRow) {
            targetGroup.lastKnownRow = sourceGroup.lastKnownRow;
        }

        if (sourceGroup.lastRow) {
            targetGroup.lastRow = sourceGroup.lastRow;
        }
    };

    const createFinalRows = (group: any) => {
        if (!group) {
            return [];
        }

        const targetUrls = getUniqueUrls(group.targetUrls);

        if (group.notifyRows.length > 0) {
            return group.notifyRows.map((notifyRow: any) => {
                const baseRow = group.lastKnownRow || notifyRow || group.lastRow;

                return {
                    ...baseRow,
                    ...notifyRow,
                    actor_user_id:
                        getRawUserId(notifyRow) ??
                        getRawUserId(baseRow) ??
                        notifyRow.actor_user_id,
                    action: 'notify',
                    criteria: notifyRow.criteria,
                    tradeoffs: notifyRow.tradeoffs,
                    application_id:
                        notifyRow.application_id ??
                        baseRow.application_id,
                    __appSettingsRowId: notifyRow.id,
                    __targetUrls: targetUrls,
                    __guestEmail: group.guestEmail,
                    __sourceRows: group.sourceRows,
                };
            });
        }

        const lastSettingsRow = group.lastSettingsRow;
        const baseRow = group.lastKnownRow || lastSettingsRow || group.lastRow;

        if (!baseRow) {
            return [];
        }

        return [
            {
                ...baseRow,
                criteria: lastSettingsRow?.criteria ?? baseRow.criteria,
                tradeoffs: lastSettingsRow?.tradeoffs ?? baseRow.tradeoffs,
                application_id:
                    lastSettingsRow?.application_id ??
                    baseRow.application_id,
                __appSettingsRowId: lastSettingsRow?.id,
                __targetUrls: targetUrls,
                __guestEmail: group.guestEmail,
                __sourceRows: group.sourceRows,
            },
        ];
    };

    rows.forEach(row => {
        const applicationId = getApplicationId(row);
        const rowIdentity = getUserFriendlyIdentity(row);

        if (rowIdentity) {
            const groupKey = getUserFriendlyGroupKey(rowIdentity, applicationId);
            const group = getOrCreateGroup(groupKey, row);
            const pendingUnknownGroup = pendingUnknownGroups.get(String(applicationId));

            if (pendingUnknownGroup && rowIdentity.type === 'guestEmail') {
                mergeGroupToGroup(group, pendingUnknownGroup);
                pendingUnknownGroups.delete(String(applicationId));
            }

            mergeRowToGroup(group, row);
            currentKnownGroupKey = groupKey;

            return;
        }

        if (currentKnownGroupKey) {
            const currentKnownGroup = groups.get(currentKnownGroupKey);
            const rowUserId = getRawUserId(row);
            const isExplicitUnknownUser = normalizeValue(rowUserId) === 'unknown';
            const canAttachToCurrentGroup =
                !isExplicitUnknownUser ||
                currentKnownGroup?.identityType === 'guestEmail';

            if (
                canAttachToCurrentGroup &&
                normalizeValue(currentKnownGroup?.applicationId) ===
                normalizeValue(applicationId)
            ) {
                mergeRowToGroup(currentKnownGroup, row);
                return;
            }
        }

        const pendingUnknownGroup = getOrCreatePendingUnknownGroup(
            String(applicationId),
            row,
        );

        mergeRowToGroup(pendingUnknownGroup, row);
    });

    const resultRows: any[] = [];

    groupKeys.forEach(key => {
        const group = groups.get(key);

        createFinalRows(group).forEach((row: any) => {
            resultRows.push(row);
        });
    });

    pendingUnknownKeys.forEach(key => {
        const group = pendingUnknownGroups.get(key);

        if (!group) {
            return;
        }

        createFinalRows(group).forEach((row: any) => {
            resultRows.push(row);
        });
    });

    return resultRows;
};

const formatValue = (
    type: FormFieldType,
    raw: any,
    placeholder: string,
    ): {
    displayValue: ReactNode;
    title: string;
    } => {
    if (isEmptyLike(raw)) {
        return {
            displayValue: (
                <Typography variant="body2" color="text.disabled">
                    {placeholder}
                </Typography>
            ),
            title: placeholder,
        };
    }

    if (type === 'date') {
        const d = dayjs(raw);

        const value = d.isValid()
            ? d.format('h:mma MM/DD/YY').toUpperCase()
            : String(raw);

        return {
            displayValue: value,
            title: value,
        };
    }

    if (type === 'color') {
        const v = String(raw);

        return {
            displayValue: (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 0.5,
                            border: '1px solid rgba(0,0,0,0.2)',
                            background: v,
                        }}
                    />

                    <Typography variant="body2">
                        {v}
                    </Typography>
                </Box>
            ),
            title: v,
        };
    }

    if (type === 'image') {
        return {
            displayValue: (
                <Box
                    component="img"
                    src={String(raw)}
                    sx={{
                        display: 'block',
                        maxHeight: 100,
                        objectFit: 'contain',
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                    }}
                />
            ),
            title: String(raw),
        };
    }

    if (type === 'video') {
        return {
            displayValue: (
                <Box
                    component="video"
                    src={String(raw)}
                    controls
                    sx={{
                        display: 'block',
                        maxHeight: 100,
                        border: '1px solid rgba(0,0,0,0.23)',
                        borderRadius: 1,
                    }}
                />
            ),
            title: String(raw),
        };
    }

    if (type === 'document') {
        const rawValue = String(raw);
        const fileName = rawValue.split('/').pop() || rawValue;

        return {
            displayValue: (
                <Link
                    to={rawValue}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {fileName}
                </Link>
            ),
            title: fileName,
        };
    }

    if (Array.isArray(raw)) {
        return {
            displayValue: raw.length > 0 ? raw.length + ' items' : placeholder,
            title: raw.length > 0 ? raw.join('\n') : placeholder,
        };
    }

    if (typeof raw === 'object') {
        const keys = Object.keys(raw);

        return {
            displayValue: keys.length > 0 ? keys.length + ' items' : placeholder,
            title: keys.length > 0 ? JSON.stringify(raw) : placeholder,
        };
    }

    return {
        displayValue: String(raw),
        title: String(raw),
    };
};

const FormsResultsTable: FC<FormsResultsTableProps> = ({
    config,
    formName,
    data,
    onEdit,
    onDelete,
    onDeleteSelected,
    maxHeight = 'unset',
    placeholder = '-',
    hasActions = false,
    isRemoveLoading = false,
    isUserFriendlyTable = false,
    isSortable = false,
    isSearchable = false,
    skin,
    }) => {
    const isSkin5 = skin === 'skin5';
    const formulaRunner = useFormulaRunner();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ imagePreview, setImagePreview ] = useState<string | null>(null);
    const responseIdsToRemove = useRef<(string | number)[]>([]);
    const fieldsKey = config.fields.map(f => f.id).join('|');

    const fieldMap = useMemo(() => {
        const map = new Map<string, FieldDef>();

        config.fields.forEach(f => map.set(f.id, f));

        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ fieldsKey ]);

    const columns = useMemo(
        () => config.fields.map(f => f.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ fieldsKey ],
    );

    const [ visibleColumnIds, setVisibleColumnIds ] = useState<Set<string>>(() => {
        const fromUrl = searchParams.get(URL_PARAM_COLUMNS)?.split(',').filter(id => columns.includes(id)) ?? [];

        return fromUrl.length > 0 ? new Set(fromUrl) : new Set(columns);
    });
    const [ columnsDialogOpen, setColumnsDialogOpen ] = useState(false);
    const [ columnsDraft, setColumnsDraft ] = useState<Set<string>>(new Set());

    const isFirstColumnsRender = useRef(true);

    useEffect(() => {
        if (isFirstColumnsRender.current) {
            isFirstColumnsRender.current = false;

            return;
        }

        setVisibleColumnIds(new Set(columns));
    }, [ columns ]);

    const displayColumns = useMemo(() => {
        if (visibleColumnIds.size === 0 || visibleColumnIds.size === columns.length) {
            return columns;
        }

        return columns.filter(col => visibleColumnIds.has(col));
    }, [ columns, visibleColumnIds ]);

    const openColumnsDialog = () => {
        setColumnsDraft(new Set(visibleColumnIds));
        setColumnsDialogOpen(true);
    };

    const closeColumnsDialog = () => {
        setColumnsDialogOpen(false);
    };

    const toggleDraftColumn = (col: string) => {
        setColumnsDraft(prev => {
            const next = new Set(prev);

            next.has(col)
                ? next.delete(col)
                : next.add(col);

            return next;
        });
    };

    const allDraftSelected = columnsDraft.size === columns.length;
    const someDraftSelected = columnsDraft.size > 0 && !allDraftSelected;

    const toggleAllDraftColumns = () => {
        setColumnsDraft(allDraftSelected ? new Set() : new Set(columns));
    };

    const applyColumnsDialog = () => {
        setVisibleColumnIds(columnsDraft);
        setColumnsDialogOpen(false);
        markDiscreteUrlSync();
    };

    const tableData = useMemo(() => {
        if (!isUserFriendlyTable) {
            return data;
        }

        const groupedRows = buildUserFriendlyRows(data);
        const externalLinkFields = config.fields.filter(isExternalLinksField);

        return applyExternalLinkStatuses(
            groupedRows,
            data,
            externalLinkFields,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ data, isUserFriendlyTable, fieldsKey ]);

    const [ selected, setSelected ] = useState<Set<RowId>>(new Set());

    const allIds = useMemo(
        () => tableData.map(r => r.id),
        [ tableData ],
    );

    const allSelected =
        selected.size > 0 &&
        selected.size === allIds.length;

    const someSelected =
        selected.size > 0 &&
        selected.size < allIds.length;

    const toggleAll = () => {
        setSelected(
            allSelected
                ? new Set()
                : new Set(allIds),
        );
    };

    const toggleOne = (id: RowId) => {
        setSelected(prev => {
            const next = new Set(prev);

            next.has(id)
                ? next.delete(id)
                : next.add(id);

            return next;
        });
    };

    const clearSelection = () => {
        setSelected(new Set());
    };

    const handleDeleteSelected = () => {
        if (onDeleteSelected && selected.size > 0) {
            setConfirmModalOpen(true);
            responseIdsToRemove.current = Array.from(selected);
        }
    };

    const onDeleteConfirm = async () => {
        await onDeleteSelected?.(responseIdsToRemove.current);
        setConfirmModalOpen(false);
        clearSelection();
    };

    const [ query, setQuery ] = useState(() => searchParams.get(URL_PARAM_SEARCH) ?? '');
    const q = query.trim().toLowerCase();

    const [ sortState, setSortState ] = useState<SortState | null>(() => {
        const col = searchParams.get(URL_PARAM_SORT_BY);
        const direction = searchParams.get(URL_PARAM_SORT_DIR);

        return col && (direction === 'asc' || direction === 'desc')
            ? { col, direction }
            : null;
    });

    const [ columnFilters, setColumnFilters ] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};

        searchParams.forEach((value, key) => {
            if (key.startsWith(URL_PARAM_FILTER_PREFIX)) {
                initial[key.slice(URL_PARAM_FILTER_PREFIX.length)] = value;
            }
        });

        return initial;
    });

    const [ filterAnchorEl, setFilterAnchorEl ] = useState<HTMLElement | null>(null);
    const filterInputRef = useRef<HTMLInputElement>(null);
    const [ filterCol, setFilterCol ] = useState<string | null>(null);

    const buildSearchParams = useCallback((prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);

        if (query.trim()) {
            next.set(URL_PARAM_SEARCH, query);
        } else {
            next.delete(URL_PARAM_SEARCH);
        }

        if (sortState) {
            next.set(URL_PARAM_SORT_BY, sortState.col);
            next.set(URL_PARAM_SORT_DIR, sortState.direction);
        } else {
            next.delete(URL_PARAM_SORT_BY);
            next.delete(URL_PARAM_SORT_DIR);
        }

        Array.from(next.keys()).filter(key => key.startsWith(URL_PARAM_FILTER_PREFIX)).forEach(key => next.delete(key));
        Object.entries(columnFilters).filter(([ , value ]) => value?.trim()).forEach(([ col, value ]) => next.set(`${URL_PARAM_FILTER_PREFIX}${col}`, value));

        const isAllColumnsVisible = visibleColumnIds.size === 0 || visibleColumnIds.size === columns.length;

        if (!isAllColumnsVisible) {
            next.set(URL_PARAM_COLUMNS, Array.from(visibleColumnIds).join(','));
        } else {
            next.delete(URL_PARAM_COLUMNS);
        }

        return next;
    }, [ query, sortState, columnFilters, visibleColumnIds, columns ]);

    const urlSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Discrete actions (sort clicks, clear filters, apply columns, blur) should feel
    // instant; free typing is debounced so we're not navigating on every keystroke.
    const isDiscreteUrlSync = useRef(false);

    const markDiscreteUrlSync = () => {
        isDiscreteUrlSync.current = true;
    };

    useEffect(() => {
        if (urlSyncTimer.current) {
            clearTimeout(urlSyncTimer.current);
        }

        const delay = isDiscreteUrlSync.current ? 0 : 400;
        isDiscreteUrlSync.current = false;

        urlSyncTimer.current = setTimeout(() => {
            urlSyncTimer.current = null;
            setSearchParams(buildSearchParams, { replace: true });
        }, delay);

        return () => {
            if (urlSyncTimer.current) {
                clearTimeout(urlSyncTimer.current);
            }
        };
    }, [ buildSearchParams, setSearchParams ]);

    const getColumnSearchText = useCallback((row: any, col: string): string => {
        if (col === 'id') {
            return String(row.id ?? '');
        }

        if (isUserFriendlyTable && col === 'criteria') {
            return getUserFriendlyEmail(row) ?? '';
        }

        const def = fieldMap.get(col);
        const type: FormFieldType = def?.type ?? 'text';
        const value = getFileValue(row, col);

        return fmt(value, type);
    }, [ fieldMap, isUserFriendlyTable ]);

    const getColumnSortValue = useCallback((row: any, col: string): string | number => {
        if (col === 'id') {
            return row.id ?? '';
        }

        if (isUserFriendlyTable && col === 'criteria') {
            return (getUserFriendlyEmail(row) ?? '').toLowerCase();
        }

        const def = fieldMap.get(col);
        const type: FormFieldType = def?.type ?? 'text';
        const value = getFileValue(row, col);

        if (isEmptyLike(value)) {
            return '';
        }

        if (type === 'date') {
            const d = dayjs(value);

            return d.isValid() ? d.valueOf() : String(value);
        }

        if (type === 'number') {
            const n = Number(value);

            return Number.isNaN(n) ? String(value) : n;
        }

        if (Array.isArray(value)) {
            return value.length;
        }

        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return String(value).toLowerCase();
    }, [ fieldMap, isUserFriendlyTable ]);

    const handleSort = (col: string) => {
        setSortState(prev => {
            if (!prev || prev.col !== col) {
                return { col, direction: 'asc' };
            }

            if (prev.direction === 'asc') {
                return { col, direction: 'desc' };
            }

            return null;
        });

        markDiscreteUrlSync();
    };

    const getSortIconComponent = (col: string) => (iconProps: any) => {
        if (sortState?.col !== col) {
            return <SwapVertIcon {...iconProps} />;
        }

        return <SortDirectionIcon direction={sortState.direction} {...iconProps} />;
    };

    const handleColumnFilterChange = (col: string, value: string) => {
        setColumnFilters(prev => ({ ...prev, [col]: value }));
    };

    const openColumnFilter = (event: MouseEvent<HTMLElement>, col: string) => {
        event.stopPropagation();
        setFilterAnchorEl(event.currentTarget);
        setFilterCol(col);
    };

    const closeColumnFilter = () => {
        setFilterAnchorEl(null);
        setFilterCol(null);
    };

    const filterColLabel = filterCol === 'id'
        ? 'Id'
        : isUserFriendlyTable && filterCol === 'criteria'
            ? 'Email'
            : fieldMap.get(filterCol ?? '')?.label || filterCol || '';

    const hasActiveColumnFilters = Object.values(columnFilters).some(v => v?.trim());
    const hasActiveFilters = hasActiveColumnFilters || !!q;

    const clearAllFilters = () => {
        setColumnFilters({});
        setQuery('');
        closeColumnFilter();
        markDiscreteUrlSync();
    };

    const filtered = useMemo(() => {
        return tableData.filter(row => {
            if (q) {
                const targetUrls = Array.isArray((row as any).__targetUrls)
                    ? (row as any).__targetUrls.join(' | ')
                    : '';

                const hay = [
                    String(row.id ?? ''),
                    String(row.userEmail ?? ''),
                    String(targetUrls),
                    dayjs(row.date).isValid()
                        ? dayjs(row.date).format('h:mma MM/DD/YY').toUpperCase()
                        : String(row.date ?? ''),
                    ...columns.map(col => getColumnSearchText(row, col)),
                ]
                .join(' | ')
                .toLowerCase();

                if (!hay.includes(q)) {
                    return false;
                }
            }

            if (isSearchable) {
                const columnMatches = [ 'id', ...columns ].every(col => {
                    const filterValue = columnFilters[col]?.trim().toLowerCase();

                    if (!filterValue) {
                        return true;
                    }

                    return getColumnSearchText(row, col)
                    .toLowerCase()
                    .includes(filterValue);
                });

                if (!columnMatches) {
                    return false;
                }
            }

            return true;
        });
    }, [ q, tableData, columns, isSearchable, columnFilters, getColumnSearchText ]);

    const sorted = useMemo(() => {
        if (!isSortable || !sortState) {
            return filtered;
        }

        const { col, direction } = sortState;

        return [ ...filtered ].sort((a, b) => {
            const va = getColumnSortValue(a, col);
            const vb = getColumnSortValue(b, col);

            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });

            return direction === 'asc' ? cmp : -cmp;
        });
    }, [ filtered, isSortable, sortState, getColumnSortValue ]);

    const handleExport = () => {
        const headerRow = [
            'Id',
            ...displayColumns.map(col => fieldMap.get(col)?.label || col),
        ];

        const dataRows = sorted.map(row => [
            String(row.id ?? ''),
            ...displayColumns.map(col => getColumnSearchText(row, col)),
        ]);

        downloadCsv(
            rowsToCsv([ headerRow, ...dataRows ]),
            `${formName}-export-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`,
        );
    };

    const getFormulaResult = (
        formula: string,
        rowData: any,
    ): string => {
        const mergedData = {};

        Object.keys(rowData).forEach(key => {
            // @ts-ignore
            mergedData[key] = {
                value: rowData[key],
                points: 1,
            };
        });

        const result = formulaRunner(formula, mergedData);

        return result as string;
    };

    const renderTargetUrlButtons = (
        urls: string[],
        placeholder: string,
    ): {
        displayValue: ReactNode;
        title: string;
    } => {
        if (!urls.length) {
            return {
                displayValue: (
                    <Typography variant="body2" color="text.disabled">
                        {placeholder}
                    </Typography>
                ),
                title: placeholder,
            };
        }

        return {
            displayValue: (
                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                >
                    {urls.map((url, index) => (
                        <Button
                            key={`${url}-${index}`}
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                                event.stopPropagation();

                                window.open(
                                    url,
                                    '_blank',
                                    'noopener,noreferrer',
                                );
                            }}
                            sx={{
                                textTransform: 'none',
                                minWidth: 72,
                            }}
                        >
                            {getLastUrlPart(url)}
                        </Button>
                    ))}
                </Stack>
            ),
            title: urls.join('\n'),
        };
    };

    const renderUserFriendlyValue = (
        field: FieldDef | undefined,
        raw: any,
        placeholder: string,
    ): {
        displayValue: ReactNode;
        title: string;
    } => {
        const type: FormFieldType = field?.type ?? 'text';

        if (type === 'url' && field) {
            const url = getUrlFieldValue(field, raw);

            if (!url) {
                return {
                    displayValue: (
                        <Typography variant="body2" color="text.disabled">
                            {placeholder}
                        </Typography>
                    ),
                    title: placeholder,
                };
            }

            return {
                displayValue: (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(event) => {
                            event.stopPropagation();

                            window.open(
                                url,
                                '_blank',
                                'noopener,noreferrer',
                            );
                        }}
                        sx={{
                            textTransform: 'none',
                            minWidth: 72,
                        }}
                    >
                        {getLastUrlPart(url)}
                    </Button>
                ),
                title: url,
            };
        }

        if (isEmptyLike(raw)) {
            return {
                displayValue: (
                    <Typography variant="body2" color="text.disabled">
                        {placeholder}
                    </Typography>
                ),
                title: placeholder,
            };
        }

        if (type === 'image') {
            const imageSrc = String(raw);

            return {
                displayValue: (
                    <Box
                        component="img"
                        src={imageSrc}
                        onClick={() => setImagePreview(imageSrc)}
                        sx={{
                            display: 'block',
                            maxWidth: 80,
                            maxHeight: 60,
                            objectFit: 'contain',
                            border: '1px solid rgba(0,0,0,0.23)',
                            borderRadius: 1,
                            cursor: 'pointer',
                        }}
                    />
                ),
                title: imageSrc,
            };
        }

        return formatValue(type, raw, placeholder);
    };

    return (
        <>
            <Paper
                sx={{
                    border: 1,
                    borderColor: isSkin5 ? '#e4ded4' : 'divider',
                    ...(isSkin5 && {
                        borderRadius: '20px',
                        boxShadow: '0 14px 40px rgba(31, 47, 43, 0.08)',
                        overflow: 'hidden',
                        color: '#22312d',
                        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        '& > .MuiBox-root:first-of-type': {
                            backgroundColor: '#fbfaf7',
                            borderColor: '#e4ded4',
                        },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#ffffff',
                            '&.Mui-focused': {
                                boxShadow: '0 0 0 3px rgba(74, 150, 123, 0.18)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4a967b',
                            },
                        },
                        '& .MuiButton-outlined': {
                            borderColor: '#4a967b',
                            borderRadius: '10px',
                            color: '#3f826d',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#3f826d',
                                backgroundColor: '#eef5f2',
                            },
                        },
                        '& .MuiButton-contained:not(.MuiButton-colorError)': {
                            borderRadius: '10px',
                            backgroundColor: '#4a967b',
                            color: '#ffffff',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#3f826d',
                                boxShadow: 'none',
                            },
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            backgroundColor: '#4a967b',
                            borderColor: '#78ad9a',
                            color: '#ffffff',
                        },
                        '& .MuiTableBody-root .MuiTableCell-root': {
                            backgroundColor: '#ffffff',
                            borderColor: '#e8e2d8',
                            color: '#22312d',
                        },
                        '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
                            backgroundColor: '#fbfaf7',
                        },
                        '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
                            backgroundColor: '#eef5f2',
                        },
                        '& .MuiTableBody-root .MuiCheckbox-root': {
                            color: '#97aaa4',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                color: '#4a967b',
                            },
                        },
                    }),
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <TextField
                            size="small"
                            placeholder="Search all fields…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onBlur={(e) => {
                                setQuery(e.target.value);
                                markDiscreteUrlSync();
                            }}
                            sx={{ width: 340 }}
                            type="search"
                        />

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<TuneIcon fontSize="small"/>}
                                onClick={openColumnsDialog}
                            >
                                Advanced filters
                            </Button>

                            {isSearchable && hasActiveFilters && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={clearAllFilters}
                                >
                                    Clear filters
                                </Button>
                            )}

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FileDownloadIcon fontSize="small"/>}
                                onClick={handleExport}
                                disabled={sorted.length === 0}
                            >
                                Export
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                <TableContainer
                    sx={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'auto',
                        maxHeight,
                        '& .MuiTableCell-stickyHeader': {
                            bgcolor: '#013b71',
                            color: 'primary.contrastText',
                        },
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            tableLayout: 'fixed',
                            borderCollapse: 'separate',
                            '& th, & td': {
                                borderBottom: 1,
                                borderColor: 'divider',
                                wordBreak: 'break-all',
                            },
                        }}
                    >
                        <colgroup>
                            <col style={{ width: CHECK_W }}/>
                            <col style={{ width: ID_W }}/>

                            {displayColumns.map((_, i) => (
                                <col
                                    key={`mid-${i}`}
                                    style={{ width: MIDDLE_W }}
                                />
                            ))}

                            {hasActions && (
                                <col style={{ width: ACTIONS_W }}/>
                            )}
                        </colgroup>

                        <TableHead>
                            <TableRow
                                sx={{
                                    '& th': {
                                        py: 0.5,
                                        fontSize: '0.85rem',
                                        backgroundColor: '#013b71',
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 4,
                                        bgcolor: '#013b71',
                                    }}
                                >
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={toggleAll}
                                        size="small"
                                        sx={{
                                            color: 'primary.contrastText',
                                            '&.Mui-checked': {
                                                color: 'primary.contrastText',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fill: 'white',
                                            },
                                        }}
                                    />
                                </TableCell>

                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: CHECK_W,
                                        zIndex: 3,
                                        bgcolor: '#013b71',
                                        color: 'white',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 700,
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        gap: 0.5,
                                    }}>
                                        {isSortable ? (
                                            <TableSortLabel
                                                active={sortState?.col === 'id'}
                                                direction={sortState?.col === 'id' ? sortState.direction : 'asc'}
                                                onClick={() => handleSort('id')}
                                                IconComponent={getSortIconComponent('id')}
                                                sx={{
                                                    color: 'inherit',
                                                    '&.MuiTableSortLabel-root:hover': { color: 'white' },
                                                    '&.Mui-active': { color: 'white' },
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: sortState?.col === 'id' ? 'white !important' : 'rgba(255,255,255,0.6) !important',
                                                        opacity: 1,
                                                        transform: 'none',
                                                    },
                                                }}
                                            >
                                                Id
                                            </TableSortLabel>
                                        ) : 'Id'}

                                        {isSearchable && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => openColumnFilter(e, 'id')}
                                                sx={{
                                                    p: 0.25,
                                                    color: columnFilters.id?.trim() ? 'white' : 'rgba(255,255,255,0.6)',
                                                }}
                                            >
                                                <SearchIcon fontSize="inherit"/>
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>

                                {displayColumns.map(col => {
                                    const def = fieldMap.get(col);
                                    const label = isUserFriendlyTable && col === 'criteria'
                                        ? 'Email'
                                        : def?.label || col;

                                    const labelNode = (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {label}
                                        </Box>
                                    );

                                    return (
                                        <TableCell
                                            key={col}
                                            title={label}
                                            sx={{
                                                bgcolor: '#013b71',
                                                color: 'white',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                gap: 0.5, minWidth: 0,
                                            }}>
                                                <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                                                    {isSortable ? (
                                                        <TableSortLabel
                                                            active={sortState?.col === col}
                                                            direction={sortState?.col === col ? sortState.direction : 'asc'}
                                                            onClick={() => handleSort(col)}
                                                            IconComponent={getSortIconComponent(col)}
                                                            sx={{
                                                                display: 'flex',
                                                                width: '100%',
                                                                minWidth: 0,
                                                                color: 'inherit',
                                                                '&.MuiTableSortLabel-root:hover': { color: 'white' },
                                                                '&.Mui-active': { color: 'white' },
                                                                '& .MuiTableSortLabel-icon': {
                                                                    color: sortState?.col === col ? 'white !important' : 'rgba(255,255,255,0.6) !important',
                                                                    opacity: 1,
                                                                    transform: 'none',
                                                                    flexShrink: 0,
                                                                },
                                                            }}
                                                        >
                                                            {labelNode}
                                                        </TableSortLabel>
                                                    ) : labelNode}
                                                </Box>

                                                {isSearchable && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openColumnFilter(e, col)}
                                                        sx={{
                                                            p: 0.25,
                                                            flexShrink: 0,
                                                            color: columnFilters[col]?.trim() ? 'white' : 'rgba(255,255,255,0.6)',
                                                        }}
                                                    >
                                                        <SearchIcon fontSize="inherit"/>
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}

                                {hasActions && (
                                    <TableCell
                                        align="right"
                                        sx={{
                                            position: 'sticky',
                                            right: 0,
                                            zIndex: 3,
                                            bgcolor: '#013b71',
                                            color: 'white',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 700,
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sorted.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={displayColumns.length + 3}
                                        sx={{ py: 3 }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            color="text.primary"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {q || (isSearchable && hasActiveColumnFilters)
                                                ? 'No results for applied filters'
                                                : 'No results'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sorted.map(row => {
                                    const checked = selected.has(row.id);

                                    const hasAppSettingsButton =
                                        isUserFriendlyTable &&
                                        shouldShowAppSettingsButton(row) &&
                                        columns.includes('criteria') &&
                                        columns.includes('tradeoffs');

                                    return (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                '&:hover td': {
                                                    backgroundColor: '#f0f6fb',
                                                },
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 2,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleOne(row.id)}
                                                    size="small"
                                                />
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: CHECK_W,
                                                    zIndex: 2,
                                                    bgcolor: 'background.paper',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-all',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                {row.id}
                                            </TableCell>

                                            {displayColumns.map(col => {
                                                const def = fieldMap.get(col);
                                                const type: FormFieldType = def?.type ?? 'text';
                                                const val = getFileValue(row, col);

                                                if (
                                                    isUserFriendlyTable &&
                                                    col === 'target_url'
                                                ) {
                                                    const urls = Array.isArray(
                                                        (row as any).__targetUrls,
                                                    )
                                                        ? (row as any).__targetUrls
                                                        : isEmptyLike(val)
                                                            ? []
                                                            : [ String(val) ];

                                                    const {
                                                        displayValue,
                                                        title,
                                                    } = renderTargetUrlButtons(
                                                        urls,
                                                        placeholder,
                                                    );

                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                verticalAlign: 'middle',
                                                                wordBreak: 'break-all',
                                                            }}
                                                            title={title}
                                                        >
                                                            {displayValue}
                                                        </TableCell>
                                                    );
                                                }

                                                if (
                                                    isUserFriendlyTable &&
                                                    col === 'action'
                                                ) {
                                                    const actionValue =
                                                        isNotifyAction(val)
                                                            ? 'Notify'
                                                            : '';

                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                verticalAlign: 'middle',
                                                                wordBreak: 'break-all',
                                                            }}
                                                            title={actionValue}
                                                        >
                                                            {actionValue}
                                                        </TableCell>
                                                    );
                                                }

                                                if (
                                                    isUserFriendlyTable &&
                                                    col === 'criteria'
                                                ) {
                                                    const email = getUserFriendlyEmail(row);
                                                    const emailTitle = email || placeholder;

                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                verticalAlign: 'middle',
                                                                wordBreak: 'break-all',
                                                            }}
                                                            title={emailTitle}
                                                        >
                                                            {email || (
                                                                <Typography variant="body2" color="text.disabled">
                                                                    {placeholder}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                }

                                                if (hasAppSettingsButton && col === 'tradeoffs') {
                                                    const appSettingsUrl = getAppSettingsUrl(row);

                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                verticalAlign: 'middle',
                                                                wordBreak: 'break-all',
                                                                textAlign: 'center',
                                                            }}
                                                            title={appSettingsUrl}
                                                        >
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();

                                                                    window.open(
                                                                        appSettingsUrl,
                                                                        '_blank',
                                                                        'noopener,noreferrer',
                                                                    );
                                                                }}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    minWidth: 160,
                                                                }}
                                                            >
                                                                App with this settings
                                                            </Button>
                                                        </TableCell>
                                                    );
                                                }

                                                if (type === 'formula') {
                                                    return (
                                                        <TableCell
                                                            key={`${row.id}-${col}`}
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                textTransform: 'capitalize',
                                                            }}
                                                        >
                                                            {getFormulaResult(
                                                                def?.formula || '',
                                                                row,
                                                            )}
                                                        </TableCell>
                                                    );
                                                }

                                                const { displayValue, title } = isUserFriendlyTable
                                                    ? renderUserFriendlyValue(
                                                        def,
                                                        val,
                                                        placeholder,
                                                    )
                                                    : formatValue(
                                                        type,
                                                        val,
                                                        placeholder,
                                                    );

                                                return (
                                                    <TableCell
                                                        key={`${row.id}-${col}`}
                                                        sx={{
                                                            bgcolor: 'background.paper',
                                                            textTransform:
                                                                (type as string) === 'url' ||
                                                                (type as string) === 'external_links'
                                                                    ? 'none'
                                                                    : 'capitalize',
                                                            verticalAlign: 'middle',
                                                            wordBreak: 'break-all',
                                                        }}
                                                        title={title}
                                                    >
                                                        {displayValue}
                                                    </TableCell>
                                                );
                                            })}

                                            {hasActions && (
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        position: 'sticky',
                                                        right: 0,
                                                        zIndex: 2,
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    {!!onEdit && (
                                                        <Tooltip title="Edit">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => onEdit(row.id)}
                                                                    disabled={!onEdit}
                                                                >
                                                                    <EditIcon
                                                                        fontSize="small"
                                                                        style={{ color: isSkin5 ? '#4a967b' : '#013b71' }}
                                                                    />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}

                                                    {!!onDelete && (
                                                        <Tooltip title="Delete">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => onDelete(row.id)}
                                                                    disabled={!onDelete}
                                                                >
                                                                    <DeleteIcon fontSize="small"/>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Popover
                    open={!!filterAnchorEl && !!filterCol}
                    anchorEl={filterAnchorEl}
                    onClose={closeColumnFilter}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{
                        transition: {
                            onEntered: () => filterInputRef.current?.focus(),
                        },
                    }}
                >
                    <Box sx={{ p: 1.5 }}>
                        <TextField
                            autoFocus
                            inputRef={filterInputRef}
                            size="small"
                            placeholder={`Search ${filterColLabel}…`}
                            value={(filterCol && columnFilters[filterCol]) ?? ''}
                            onChange={(e) => filterCol && handleColumnFilterChange(filterCol, e.target.value)}
                            onBlur={(e) => {
                                if (filterCol) {
                                    handleColumnFilterChange(filterCol, e.target.value);
                                }

                                markDiscreteUrlSync();
                            }}
                            type="search"
                            sx={{ width: 220 }}
                        />
                    </Box>
                </Popover>

                {selected.size > 0 && hasActions && (
                    <Box
                        sx={{
                            bottom: 0,
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {selected.size} selected
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="text"
                                onClick={clearSelection}
                            >
                                Unselect all
                            </Button>

                            <Button
                                startIcon={<DeleteIcon fontSize="small"/>}
                                variant="contained"
                                color="error"
                                onClick={handleDeleteSelected}
                                disabled={!onDeleteSelected}
                            >
                                Delete selected
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>

            <ConfirmModal
                isLoading={isRemoveLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />

            <Dialog
                open={!!imagePreview}
                onClose={() => setImagePreview(null)}
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            width: {
                                xs: 'calc(100vw - 32px)',
                                sm: 'auto',
                            },
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            margin: {
                                xs: '16px',
                                sm: '32px',
                            },
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: {
                            xs: '8px',
                            sm: '16px',
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                    }}
                >
                    {imagePreview && (
                        <Box
                            component="img"
                            src={imagePreview}
                            sx={{
                                display: 'block',
                                width: '100%',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: {
                                    xs: 'calc(90vh - 16px)',
                                    sm: 'calc(90vh - 32px)',
                                },
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={columnsDialogOpen}
                onClose={closeColumnsDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Columns to display</DialogTitle>

                <DialogContent dividers>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allDraftSelected}
                                    indeterminate={someDraftSelected}
                                    onChange={toggleAllDraftColumns}
                                />
                            }
                            label={allDraftSelected ? 'Unselect all' : 'Select all'}
                        />

                        <Divider sx={{ my: 1 }}/>

                        {columns.map(col => {
                            const def = fieldMap.get(col);
                            const label = isUserFriendlyTable && col === 'criteria'
                                ? 'Email'
                                : def?.label || col;

                            return (
                                <FormControlLabel
                                    key={col}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={columnsDraft.has(col)}
                                            onChange={() => toggleDraftColumn(col)}
                                        />
                                    }
                                    label={
                                        <Box
                                            component="span"
                                            title={label}
                                            sx={{
                                                display: 'inline-block',
                                                maxWidth: 300,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            {label}
                                        </Box>
                                    }
                                />
                            );
                        })}
                    </FormGroup>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeColumnsDialog}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={applyColumnsDialog}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FormsResultsTable;